/**
 * Shared Add/Edit Campaign form helpers (campaign_frontend.pdf).
 * Control-plane API is the source of truth — no invented PD-only fields.
 */

export const DIALING_TYPES = ["ai_bot", "predictive", "manual", "bot"];
/** User-facing labels (API values stay ai_bot / bot). */
export const DIALING_TYPE_LABELS = {
  ai_bot: "VICI Bot",
  predictive: "Predictive",
  manual: "Manual",
  bot: "AI Agent",
};
export function dialingTypeLabel(value) {
  const key = String(value || "ai_bot").trim().toLowerCase();
  return DIALING_TYPE_LABELS[key] || key.replace(/_/g, " ");
}

/** New Campaign picker cards — order matches product UX. */
export const DIALING_TYPE_PICKER = [
  {
    value: "predictive",
    label: "Predictive",
    blurb: "Auto-dials ahead of agents and connects live answers to Ready agents.",
  },
  {
    value: "manual",
    label: "Manual",
    blurb: "Agents dial the next lead or enter a number themselves from the workspace.",
  },
  {
    value: "ai_bot",
    label: "VICI Bot",
    blurb: "Outbound through VICIdial with your flow and AMD handling on the bot path.",
  },
  {
    value: "bot",
    label: "AI Agent",
    blurb: "AI-handled conversations with dial rate control and agent escalation options.",
  },
];
export const CAMPAIGN_TYPES = ["outbound", "inbound", "blended"];
export const DIAL_STATUS_OPTIONS = [
  "NEW", "QUEUE", "DIALING", "ANSWERED", "BUSY", "NOANSWER",
  "AA", "DC", "LRERR", "FAILED", "DROP", "PDROP",
  "NI", "SALE", "XFER", "DNC", "COMPLETED", "CALLBACK",
];
export const DEFAULT_DIAL_STATUSES = ["NEW", "QUEUE", "CALLBACK"];
/** System wrap-up catalog codes (DNC excluded from pickers). */
export const SYSTEM_DIAL_CATALOG_CODES = [
  "ANSWERED", "BUSY", "NOANSWER", "FAILED", "COMPLETED", "CALLBACK",
  "SALE", "NI", "XFER",
];
export const DEFAULT_LIST_DIAL_STATUSES = ["NEW", "QUEUE"];
export const DEFAULT_SYSTEM_DIAL_STATUSES = ["CALLBACK"];
export const AMD_ACTIONS = ["hangup", "voicemail", "log_only"];

/** Union of list + system dial statuses for the dialer (DNC never included). */
export function mergeDialStatuses(listDialStatuses, systemDialStatuses) {
  const out = [];
  for (const raw of [
    ...(Array.isArray(listDialStatuses) ? listDialStatuses : []),
    ...(Array.isArray(systemDialStatuses) ? systemDialStatuses : []),
  ]) {
    const code = String(raw || "").trim().toUpperCase();
    if (!code || code === "DNC" || out.includes(code)) continue;
    out.push(code);
  }
  return out;
}

/**
 * Split a flat dial_statuses list into list vs system buckets for the form.
 * Codes in the system/custom catalog → systemDialStatuses; everything else → list.
 */
export function splitDialStatuses(allStatuses, systemCatalogCodes = SYSTEM_DIAL_CATALOG_CODES) {
  // Only wrap-up catalog codes count as "system" for hydrate split.
  // NEW / QUEUE / DIALING and any list-only statuses go to listDialStatuses.
  const catalog = new Set(
    (systemCatalogCodes || SYSTEM_DIAL_CATALOG_CODES).map((c) => String(c).toUpperCase()),
  );
  const listDialStatuses = [];
  const systemDialStatuses = [];
  for (const raw of Array.isArray(allStatuses) ? allStatuses : []) {
    const code = String(raw || "").trim().toUpperCase();
    if (!code || code === "DNC") continue;
    if (catalog.has(code)) {
      if (!systemDialStatuses.includes(code)) systemDialStatuses.push(code);
    } else if (!listDialStatuses.includes(code)) {
      listDialStatuses.push(code);
    }
  }
  return { listDialStatuses, systemDialStatuses };
}
export const CALL_RECORDING_MODES = ["never", "always", "on_demand"];
export const AGENT_CALL_ROUTING = ["random", "less_calls", "high_calls", "agent_level"];
export const AGENT_LEVEL_ROUTING = ["highest", "lowest"];

/** never | always | on_demand — maps the legacy recording_enabled bool. */
export function normalizeCallRecording(value, recordingEnabled) {
  const v = String(value || "").trim().toLowerCase().replace(/[-\s]/g, "_");
  if (v === "on_demand" || v === "ondemand") return "on_demand";
  if (v === "always" || v === "on" || v === "true" || v === "1" || v === "yes") return "always";
  if (v === "never" || v === "off" || v === "false" || v === "0" || v === "no") return "never";
  if (recordingEnabled === true) return "always";
  return "never";
}

/** Digits only. Empty = prefix-dial disabled for this campaign. */
export function normalizeDialingPrefix(value) {
  return String(value || "").replace(/\D/g, "").slice(0, 8);
}

/** Empty / 0 = start as soon as the call is connected. Only used when Always. */
export function normalizeRecordingStartDelaySec(value, mode, recordingEnabled) {
  if (normalizeCallRecording(mode, recordingEnabled) !== "always") return null;
  if (value === "" || value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(Math.floor(n), 3600);
}

export const AMD_DEFAULTS = {
  enabled: false,
  timeout: "3200",
  action: "hangup",
  greetingMax: "4500",
  vmAudioId: "",
  vmFilename: "",
};

/** Common IANA zones for the campaign timezone smart-search (matches customer-settings). */
export const COMMON_TIMEZONES = [
  "UTC",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Mexico_City", "America/Sao_Paulo",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
  "Europe/Amsterdam", "Europe/Warsaw", "Europe/Istanbul", "Europe/Moscow",
  "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Dhaka",
  "Asia/Bangkok", "Asia/Singapore", "Asia/Shanghai", "Asia/Tokyo",
  "Asia/Seoul", "Australia/Sydney", "Pacific/Auckland",
];

export function needsLeadLists(dialingType) {
  return dialingType === "predictive" || dialingType === "manual" || dialingType === "bot";
}

export function needsAgentSession(dialingType) {
  return dialingType === "predictive" || dialingType === "manual";
}

/** User groups: Predictive/Manual (with Agent Session) and Bot (groups only). */
export function needsUserGroups(dialingType) {
  return dialingType === "predictive" || dialingType === "manual" || dialingType === "bot";
}

export function needsCampaignType(dialingType) {
  return dialingType === "predictive" || dialingType === "manual" || dialingType === "bot";
}

/** Predictive: concurrent lines per ready agent. */
export function needsDialLevel(dialingType) {
  return dialingType === "predictive";
}

/** AI Agent (bot): calls per second (CPS). */
export function needsDialRate(dialingType) {
  return dialingType === "bot";
}

/** PD server — Predictive + Manual + AI Agent. */
export function needsPdServer(dialingType) {
  return dialingType === "predictive" || dialingType === "manual" || dialingType === "bot";
}

export const WORKING_DAY_OPTIONS = [
  { id: "mon", label: "Mon" },
  { id: "tue", label: "Tue" },
  { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" },
  { id: "fri", label: "Fri" },
  { id: "sat", label: "Sat" },
  { id: "sun", label: "Sun" },
];

/** HH:MM → minutes since midnight; null if invalid. */
export function hhmmToMinutes(value) {
  const s = String(value || "").trim();
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) return null;
  return h * 60 + min;
}

export function normalizeDialingWindows(windows) {
  const out = [];
  for (const w of Array.isArray(windows) ? windows : []) {
    const start = String(w?.start || "").trim();
    const end = String(w?.end || "").trim();
    if (!start && !end) continue;
    out.push({ start, end });
  }
  return out;
}

export function normalizeWorkingDays(days) {
  const allowed = new Set(WORKING_DAY_OPTIONS.map((d) => d.id));
  const out = [];
  for (const raw of Array.isArray(days) ? days : []) {
    const id = String(raw || "").trim().toLowerCase();
    if (allowed.has(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

/** Validate multi dialing windows: each start < end; no overlaps. */
export function validateDialingWindows(windows) {
  const cleaned = normalizeDialingWindows(windows);
  for (const w of cleaned) {
    if (!w.start || !w.end) {
      return { ok: false, error: "Each dialing time needs both a start and an end (HH:MM)." };
    }
    const a = hhmmToMinutes(w.start);
    const b = hhmmToMinutes(w.end);
    if (a == null || b == null) {
      return { ok: false, error: "Dialing times must use HH:MM format." };
    }
    if (a >= b) {
      return { ok: false, error: "Dialing start must be before dialing end (same day)." };
    }
  }
  const ordered = [...cleaned].sort(
    (x, y) => hhmmToMinutes(x.start) - hhmmToMinutes(y.start),
  );
  for (let i = 1; i < ordered.length; i += 1) {
    const prev = ordered[i - 1];
    const cur = ordered[i];
    if (hhmmToMinutes(cur.start) < hhmmToMinutes(prev.end)) {
      return {
        ok: false,
        error: `Dialing times overlap (${prev.start}–${prev.end} and ${cur.start}–${cur.end}). Times must not overlap.`,
      };
    }
  }
  return { ok: true, windows: cleaned };
}

/** Ordered PD server ids from the form (multi-select + legacy single). */
export function selectedPdServerIds(form = {}) {
  const ids = [];
  for (const raw of Array.isArray(form.pdServerIds) ? form.pdServerIds : []) {
    if (raw && !ids.includes(raw)) ids.push(raw);
  }
  if (form.pdServerId && !ids.includes(form.pdServerId)) ids.unshift(form.pdServerId);
  return ids;
}

/** Telephony IP + Media server are required for agent/bot dialing types. */
export function needsTelephonyIp(dialingType) {
  return dialingType === "ai_bot" || dialingType === "predictive"
    || dialingType === "manual" || dialingType === "bot";
}

export function needsMediaServer(dialingType) {
  return needsTelephonyIp(dialingType);
}

/** Ordered Telephony IP (OpenSIPS) ids — first is Primary. */
export function selectedOpensipsIds(form = {}) {
  const ids = [];
  for (const raw of Array.isArray(form.opensipsIds) ? form.opensipsIds : []) {
    if (raw && raw !== "__none__" && !ids.includes(raw)) ids.push(raw);
  }
  if (form.opensipsId && form.opensipsId !== "__none__" && !ids.includes(form.opensipsId)) {
    ids.unshift(form.opensipsId);
  }
  return ids;
}

/** Ordered media server ids — first is Primary. */
export function selectedMediaServerIds(form = {}) {
  const ids = [];
  for (const raw of Array.isArray(form.mediaServerIds) ? form.mediaServerIds : []) {
    if (raw && !ids.includes(raw)) ids.push(raw);
  }
  if (form.mediaServerId && !ids.includes(form.mediaServerId)) {
    ids.unshift(form.mediaServerId);
  }
  return ids;
}

export function needsVicidial(dialingType) {
  return dialingType === "ai_bot";
}

export function needsInboundGroups(campaignType) {
  return campaignType === "inbound" || campaignType === "blended" || campaignType === "outbound";
}

export function needsVendor(dialingType) {
  return dialingType === "predictive" || dialingType === "bot" || dialingType === "manual";
}

/** Schedule UI — not used for VICI Bot (ai_bot). Blank start+end = 24×7 elsewhere. */
export function needsSchedule(dialingType) {
  return dialingType !== "ai_bot";
}

/** Predictive, or inbound/blended of any dialing type. */
export function needsAgentCallRouting(dialingType, campaignType) {
  return dialingType === "predictive"
    || campaignType === "inbound"
    || campaignType === "blended";
}

export function normalizeAgentCallRouting(value) {
  const v = String(value || "").trim().toLowerCase().replace(/[-\s]/g, "_");
  if (v === "less_calls" || v === "lesscalls" || v === "less") return "less_calls";
  if (v === "high_calls" || v === "highcalls" || v === "high") return "high_calls";
  if (v === "agent_level" || v === "agentlevel" || v === "level") return "agent_level";
  return "random";
}

/** Empty unless Agent Call Routing is Agent level. Default Highest level. */
export function normalizeAgentLevelRouting(value, callRouting) {
  if (normalizeAgentCallRouting(callRouting) !== "agent_level") return "";
  const v = String(value || "").trim().toLowerCase();
  if (v === "lowest") return "lowest";
  return "highest";
}

/** AMD: Basic for AI Bot; Advance for Predictive / Bot. Not used for Manual. */
export function needsAmd(dialingType) {
  return dialingType === "ai_bot" || dialingType === "bot"
    || dialingType === "predictive";
}

/** AMD sits on Basic only for AI Bot; otherwise Advance. */
export function amdOnBasicTab(dialingType) {
  return dialingType === "ai_bot";
}

/** Advanced PD dialing controls (retry/callback/concurrency/pacing). */
export function needsAdvancedDialing(dialingType) {
  return dialingType === "predictive" || dialingType === "bot" || dialingType === "manual";
}

export function normalizeAgentSession(v) {
  if (v === "meetme" || v === "conference") return "conference";
  if (v === "direct_phone") return "direct_phone";
  return "conference";
}

export function normalizeAmdAction(v) {
  if (v === "hangup" || v === "voicemail" || v === "log_only") return v;
  return "hangup";
}

/** Active groups plus any currently selected (so edit restore works for inactive). */
export function visibleInboundGroups(inboundGroups, selectedIds = []) {
  const selected = new Set(selectedIds || []);
  return (inboundGroups || []).filter(
    (g) => g.status === "active" || selected.has(g.id),
  );
}

export function emptyCampaignFormState(overrides = {}) {
  return {
    name: "",
    dialingType: "ai_bot",
    campaignType: "outbound",
    opensipsId: "__none__",
    opensipsIds: [],
    mediaServerId: "",
    mediaServerIds: [],
    vicidialId: "",
    pdServerId: "",
    pdServerIds: [],
    dialLevel: "",
    /** AI Agent (bot) calls per second. */
    dialRate: "",
    /** Predictive abandon hard-stop ratio (0–1]; empty = unprotected. */
    abandonRateLimit: "",
    /** P0-05 — pacing uses abandon_rate_incl_amd when true (default off). */
    pacingUseAbandonRateInclAmd: false,
    agentSession: "conference",
    scheduleStart: "",
    scheduleEnd: "",
    /** AI Agent multi dialing windows [{start, end}, ...]. */
    dialingWindows: [{ start: "", end: "" }],
    /** Weekday codes mon..sun; empty = every day. */
    workingDays: [],
    timezone: "",
    inboundGroupIds: [],
    agentGroupIds: [],
    agentUserIds: [],
    /** "user" | "group" — Predictive wizard Assigned to mode. */
    assignMode: "group",
    listIds: [],
    /** Statuses selected from leads on assigned lists. */
    listDialStatuses: [...DEFAULT_LIST_DIAL_STATUSES],
    /** Statuses selected from system/custom disposition catalog. */
    systemDialStatuses: [...DEFAULT_SYSTEM_DIAL_STATUSES],
    /** Union used by dialer / API (list + system, no DNC). */
    dialStatuses: mergeDialStatuses(DEFAULT_LIST_DIAL_STATUSES, DEFAULT_SYSTEM_DIAL_STATUSES),
    vendorId: "__none__",
    callRecording: "never",
    recordingStartDelaySec: "",
    dialingPrefix: "",
    agentCallRouting: "random",
    agentLevelRouting: "",
    callerId: "",
    maxDur: "",
    amdEnabled: false,
    amdTimeout: "3200",
    amdAction: "hangup",
    amdGreetingMax: "4500",
    vmAudioId: "",
    vmFilename: "",
    callbackEnabled: true,
    ...overrides,
  };
}

/** Hydrate form state from a campaign API document. */
export function hydrateFromCampaign(camp) {
  if (!camp) return emptyCampaignFormState();
  return emptyCampaignFormState({
    id: camp.id,
    name: camp.name || "",
    dialingType: camp.dialing_type || "ai_bot",
    campaignType: camp.campaign_type || "outbound",
    opensipsId: camp.opensips_server_id || "__none__",
    opensipsIds: Array.isArray(camp.opensips_server_ids) && camp.opensips_server_ids.length
      ? [...camp.opensips_server_ids]
      : (camp.opensips_server_id ? [camp.opensips_server_id] : []),
    mediaServerId: camp.media_server_id || "",
    mediaServerIds: Array.isArray(camp.media_server_ids) && camp.media_server_ids.length
      ? [...camp.media_server_ids]
      : (camp.media_server_id ? [camp.media_server_id] : []),
    vicidialId: camp.vicidial_server_id || "",
    pdServerId: camp.pd_server_id || "",
    pdServerIds: Array.isArray(camp.pd_server_ids) && camp.pd_server_ids.length
      ? [...camp.pd_server_ids]
      : (camp.pd_server_id ? [camp.pd_server_id] : []),
    dialLevel: camp.dial_level != null && camp.dial_level !== ""
      ? String(camp.dial_level)
      : ((camp.dialing_type || "ai_bot") === "predictive" ? "1" : ""),
    dialRate: camp.dial_rate != null && camp.dial_rate !== ""
      ? String(camp.dial_rate)
      : ((camp.dialing_type || "") === "bot"
        // Legacy: older bot campaigns stored CPS in dial_level only.
        ? (camp.dial_level != null && camp.dial_level !== "" ? String(camp.dial_level) : "1")
        : ""),
    abandonRateLimit: camp.abandon_rate_limit != null && camp.abandon_rate_limit !== ""
      ? String(camp.abandon_rate_limit)
      : "",
    pacingUseAbandonRateInclAmd: Boolean(camp.pacing_use_abandon_rate_incl_amd),
    agentSession: normalizeAgentSession(camp.agent_session),
    scheduleStart: camp.schedule_start || "",
    scheduleEnd: camp.schedule_end || "",
    dialingWindows: (() => {
      const wins = normalizeDialingWindows(camp.dialing_windows);
      if (wins.length) return wins;
      if (camp.schedule_start || camp.schedule_end) {
        return [{ start: camp.schedule_start || "", end: camp.schedule_end || "" }];
      }
      return [{ start: "", end: "" }];
    })(),
    workingDays: normalizeWorkingDays(camp.working_days),
    timezone: camp.timezone || "",
    inboundGroupIds: Array.isArray(camp.inbound_group_ids) ? [...camp.inbound_group_ids] : [],
    agentGroupIds: Array.isArray(camp.agent_group_ids) ? [...camp.agent_group_ids] : [],
    agentUserIds: Array.isArray(camp.agent_user_ids) ? [...camp.agent_user_ids] : [],
    assignMode: (Array.isArray(camp.agent_user_ids) && camp.agent_user_ids.length)
      ? "user"
      : "group",
    listIds: Array.isArray(camp.list_ids) ? [...camp.list_ids] : [],
    ...(() => {
      const all = Array.isArray(camp.dial_statuses) && camp.dial_statuses.length
        ? camp.dial_statuses.map((s) => String(s).toUpperCase()).filter((s) => s && s !== "DNC")
        : [...DEFAULT_DIAL_STATUSES];
      const storedList = Array.isArray(camp.list_dial_statuses)
        ? camp.list_dial_statuses.map((s) => String(s).toUpperCase()).filter((s) => s && s !== "DNC")
        : null;
      const storedSystem = Array.isArray(camp.system_dial_statuses)
        ? camp.system_dial_statuses.map((s) => String(s).toUpperCase()).filter((s) => s && s !== "DNC")
        : null;
      const split = (storedList != null || storedSystem != null)
        ? {
          listDialStatuses: storedList || [],
          systemDialStatuses: storedSystem || [],
        }
        : splitDialStatuses(all);
      return {
        listDialStatuses: split.listDialStatuses,
        systemDialStatuses: split.systemDialStatuses,
        dialStatuses: mergeDialStatuses(split.listDialStatuses, split.systemDialStatuses).length
          ? mergeDialStatuses(split.listDialStatuses, split.systemDialStatuses)
          : all,
      };
    })(),
    vendorId: camp.vendor_id || "__none__",
    callRecording: normalizeCallRecording(camp.call_recording, camp.recording_enabled),
    recordingStartDelaySec: (() => {
      const mode = normalizeCallRecording(camp.call_recording, camp.recording_enabled);
      const n = normalizeRecordingStartDelaySec(
        camp.recording_start_delay_sec, mode, camp.recording_enabled,
      );
      return n == null ? "" : String(n);
    })(),
    dialingPrefix: normalizeDialingPrefix(camp.dialing_prefix),
    agentCallRouting: normalizeAgentCallRouting(camp.agent_call_routing),
    agentLevelRouting: normalizeAgentLevelRouting(
      camp.agent_level_routing, camp.agent_call_routing,
    ),
    callerId: camp.caller_id || "",
    maxDur: camp.max_call_duration_sec ? String(camp.max_call_duration_sec) : "",
    amdEnabled: !!camp.amd_enabled,
    amdTimeout: camp.amd_decision_timeout_ms ? String(camp.amd_decision_timeout_ms) : "3200",
    amdAction: normalizeAmdAction(camp.amd_machine_action),
    amdGreetingMax: camp.amd_greeting_max_ms ? String(camp.amd_greeting_max_ms) : "4500",
    vmAudioId: camp.amd_voicemail_audio_id || "",
    vmFilename: camp.amd_voicemail_filename || "",
    callbackEnabled: camp.callback_enabled !== false,
  });
}

/**
 * Validate form state. Returns { ok: true } or { ok: false, error: string }.
 * @param {object} form
 * @param {{ pdServerCount?: number }} opts
 */
export function validateCampaignForm(form, opts = {}) {
  const pdServerCount = opts.pdServerCount ?? 0;
  if (!(form.name || "").trim()) {
    return { ok: false, error: "Enter a campaign name" };
  }
  if (needsVicidial(form.dialingType) && !form.vicidialId) {
    return { ok: false, error: "Select a VICIdial server" };
  }
  const pdIds = selectedPdServerIds(form);
  if (form.dialingType === "manual") {
    if (pdIds.length === 0) {
      return { ok: false, error: "Select at least one PD server" };
    }
  } else if (needsPdServer(form.dialingType) && pdIds.length === 0 && pdServerCount !== 1) {
    return { ok: false, error: "Select at least one PD server" };
  }
  if (needsVendor(form.dialingType) && (!form.vendorId || form.vendorId === "__none__")) {
    return { ok: false, error: "Select a vendor route" };
  }
  if (needsTelephonyIp(form.dialingType) && selectedOpensipsIds(form).length === 0) {
    return { ok: false, error: "Select at least one Telephony IP" };
  }
  if (needsMediaServer(form.dialingType) && selectedMediaServerIds(form).length === 0) {
    return { ok: false, error: "Select at least one media server" };
  }
  if (needsAmd(form.dialingType) && form.amdEnabled && form.amdAction === "voicemail" && !form.vmAudioId) {
    return { ok: false, error: "Upload a voicemail drop audio file for the Leave voicemail action" };
  }
  if (needsDialLevel(form.dialingType)) {
    if (form.dialLevel === "" || form.dialLevel == null) {
      return { ok: false, error: "Enter a Dial Level" };
    }
    const n = Number(form.dialLevel);
    if (!Number.isFinite(n) || n <= 0) {
      return { ok: false, error: "Dial Level must be a positive number" };
    }
  } else if (form.dialLevel !== "" && form.dialLevel != null) {
    const n = Number(form.dialLevel);
    if (!Number.isFinite(n) || n <= 0) {
      return { ok: false, error: "Dial level must be a positive number" };
    }
  }
  if (needsDialRate(form.dialingType)) {
    if (form.dialRate === "" || form.dialRate == null) {
      return { ok: false, error: "Enter a Dial Rate" };
    }
    const n = Number(form.dialRate);
    if (!Number.isFinite(n) || n <= 0) {
      return { ok: false, error: "Dial Rate must be a positive number" };
    }
  } else if (form.dialRate !== "" && form.dialRate != null) {
    const n = Number(form.dialRate);
    if (!Number.isFinite(n) || n <= 0) {
      return { ok: false, error: "Dial Rate must be a positive number" };
    }
  }
  if (form.dialingType === "bot" || (Array.isArray(form.dialingWindows) && form.dialingWindows.some((w) => w?.start || w?.end))) {
    const winCheck = validateDialingWindows(form.dialingWindows);
    if (!winCheck.ok) return winCheck;
  }
  if (form.maxDur !== "" && form.maxDur != null) {
    const d = Number(form.maxDur);
    if (!Number.isFinite(d) || d < 1 || d > 3600) {
      return { ok: false, error: "Max call duration must be between 1 and 3600 seconds" };
    }
  }
  return { ok: true };
}

function applyAdvancedDialingToBody(body, form, { clearEmpty = false } = {}) {
  if (!needsAdvancedDialing(form.dialingType) && !needsAmd(form.dialingType)) {
    return;
  }
  if (form.callbackEnabled !== undefined && form.callbackEnabled !== null) {
    body.callback_enabled = !!form.callbackEnabled;
  }
}

function applyAmdToBody(body, form) {
  // Manual campaigns do not use AMD — always persist off.
  if (form.dialingType === "manual") {
    body.amd_enabled = false;
    body.amd_decision_timeout_ms = 3200;
    body.amd_machine_action = "hangup";
    body.amd_greeting_max_ms = 4500;
    body.amd_voicemail_audio_id = "";
    body.amd_voicemail_filename = "";
    return;
  }
  body.amd_enabled = !!form.amdEnabled;
  body.amd_decision_timeout_ms = Number(form.amdTimeout) || 3200;
  body.amd_machine_action = normalizeAmdAction(form.amdAction);
  body.amd_greeting_max_ms = Number(form.amdGreetingMax) || 4500;
  body.amd_voicemail_audio_id = form.vmAudioId || "";
  body.amd_voicemail_filename = form.vmFilename || "";
}

/**
 * Build POST /campaigns body from form state.
 * Omits empty optionals (create semantics).
 */
export function buildCreateCampaignBody(form) {
  const body = {
    name: (form.name || "").trim(),
    dialing_type: form.dialingType,
    campaign_type: form.campaignType,
    call_recording: normalizeCallRecording(form.callRecording, form.recordingEnabled),
    recording_enabled: normalizeCallRecording(form.callRecording, form.recordingEnabled) === "always",
    recording_start_delay_sec: normalizeRecordingStartDelaySec(
      form.recordingStartDelaySec, form.callRecording, form.recordingEnabled,
    ),
    caller_id: (form.callerId || "").trim() || null,
    dialing_prefix: needsVendor(form.dialingType)
      ? (normalizeDialingPrefix(form.dialingPrefix) || null)
      : null,
  };
  if (needsAgentCallRouting(form.dialingType, form.campaignType)) {
    const routing = normalizeAgentCallRouting(form.agentCallRouting);
    body.agent_call_routing = routing;
    body.agent_level_routing = routing === "agent_level"
      ? (normalizeAgentLevelRouting(form.agentLevelRouting, routing) || "highest")
      : null;
  }
  if (needsVicidial(form.dialingType)) body.vicidial_server_id = form.vicidialId;
  else if (form.vicidialId) body.vicidial_server_id = form.vicidialId;

  if (needsPdServer(form.dialingType)) {
    const pdIds = selectedPdServerIds(form);
    if (pdIds.length) {
      body.pd_server_ids = pdIds;
      body.pd_server_id = pdIds[0];
    }
  }
  if (needsDialLevel(form.dialingType)) {
    const n = Number(form.dialLevel);
    body.dial_level = Number.isFinite(n) && n > 0 ? n : 1;
  }
  if (needsDialRate(form.dialingType)) {
    const n = Number(form.dialRate);
    body.dial_rate = Number.isFinite(n) && n > 0 ? n : 1;
  }
  // Issue 3A — optional; omit when blank so campaigns stay unprotected by default.
  if (form.dialingType === "predictive" && form.abandonRateLimit !== "" && form.abandonRateLimit != null) {
    const ar = Number(form.abandonRateLimit);
    if (Number.isFinite(ar) && ar > 0 && ar <= 1) {
      body.abandon_rate_limit = ar;
    }
  }
  if (form.dialingType === "predictive") {
    body.pacing_use_abandon_rate_incl_amd = Boolean(form.pacingUseAbandonRateInclAmd);
  }
  if (needsAgentSession(form.dialingType)) {
    body.agent_session = form.agentSession;
  }
  if (needsUserGroups(form.dialingType)) {
    const mode = form.assignMode === "user" ? "user" : "group";
    if (mode === "user") {
      body.agent_user_ids = form.agentUserIds || [];
      body.agent_group_ids = [];
    } else {
      body.agent_group_ids = form.agentGroupIds || [];
      body.agent_user_ids = [];
    }
  }
  if (needsTelephonyIp(form.dialingType) || selectedOpensipsIds(form).length) {
    const opsIds = selectedOpensipsIds(form);
    if (opsIds.length) {
      body.opensips_server_ids = opsIds;
      body.opensips_server_id = opsIds[0];
    }
  }
  if (needsMediaServer(form.dialingType) || selectedMediaServerIds(form).length) {
    const medIds = selectedMediaServerIds(form);
    if (medIds.length) {
      body.media_server_ids = medIds;
      body.media_server_id = medIds[0];
    }
  }
  if (needsSchedule(form.dialingType)) {
    const winCheck = validateDialingWindows(form.dialingWindows);
    const windows = winCheck.ok ? (winCheck.windows || []) : [];
    if (form.dialingType === "bot" || windows.length) {
      body.dialing_windows = windows;
      body.working_days = normalizeWorkingDays(form.workingDays);
      body.schedule_start = windows[0]?.start || form.scheduleStart || "";
      body.schedule_end = windows[0]?.end || form.scheduleEnd || "";
    } else {
      if (form.scheduleStart) body.schedule_start = form.scheduleStart;
      if (form.scheduleEnd) body.schedule_end = form.scheduleEnd;
    }
  }
  if ((form.timezone || "").trim()) body.timezone = form.timezone.trim();
  // Always send when inbound/blended so multi-select (incl. empty) persists.
  if (needsInboundGroups(form.campaignType)) {
    body.inbound_group_ids = form.inboundGroupIds || [];
  }
  if (needsLeadLists(form.dialingType)) {
    body.list_ids = form.listIds || [];
    const merged = mergeDialStatuses(form.listDialStatuses, form.systemDialStatuses);
    body.dial_statuses = merged.length
      ? merged
      : (form.dialStatuses || []).filter((s) => s && s !== "DNC");
    body.list_dial_statuses = (form.listDialStatuses || []).filter((s) => s && s !== "DNC");
    body.system_dial_statuses = (form.systemDialStatuses || []).filter((s) => s && s !== "DNC");
  }
  if (needsVendor(form.dialingType) && form.vendorId && form.vendorId !== "__none__") {
    body.vendor_id = form.vendorId;
  }
  body.max_call_duration_sec = form.maxDur ? Number(form.maxDur) : null;
  if (needsAmd(form.dialingType)) applyAmdToBody(body, form);
  applyAdvancedDialingToBody(body, form);
  return body;
}

/**
 * Build PUT /campaigns/{id} body from form state.
 * Explicitly clears inapplicable fields so unrelated settings are not erased incorrectly,
 * while type switches update visibility-related fields per backend update semantics.
 */
export function buildUpdateCampaignBody(form, opts = {}) {
  const pdServers = opts.pdServers || [];
  const body = {
    name: (form.name || "").trim(),
    dialing_type: form.dialingType,
    campaign_type: form.campaignType,
    opensips_server_id: (() => {
      const opsIds = selectedOpensipsIds(form);
      return opsIds[0] || (form.opensipsId === "__none__" ? "" : (form.opensipsId || ""));
    })(),
    opensips_server_ids: selectedOpensipsIds(form),
    media_server_id: selectedMediaServerIds(form)[0] || form.mediaServerId || "",
    media_server_ids: selectedMediaServerIds(form),
    max_call_duration_sec: form.maxDur ? Number(form.maxDur) : 0,
    ...(() => {
      if (!needsSchedule(form.dialingType)) {
        return { schedule_start: "", schedule_end: "", dialing_windows: [], working_days: [] };
      }
      const winCheck = validateDialingWindows(form.dialingWindows);
      const windows = winCheck.ok ? (winCheck.windows || []) : [];
      if (form.dialingType === "bot" || windows.length) {
        return {
          dialing_windows: windows,
          working_days: normalizeWorkingDays(form.workingDays),
          schedule_start: windows[0]?.start || form.scheduleStart || "",
          schedule_end: windows[0]?.end || form.scheduleEnd || "",
        };
      }
      return {
        schedule_start: form.scheduleStart || "",
        schedule_end: form.scheduleEnd || "",
      };
    })(),
    timezone: (form.timezone || "").trim(),
    call_recording: normalizeCallRecording(form.callRecording, form.recordingEnabled),
    recording_enabled: normalizeCallRecording(form.callRecording, form.recordingEnabled) === "always",
    recording_start_delay_sec: normalizeRecordingStartDelaySec(
      form.recordingStartDelaySec, form.callRecording, form.recordingEnabled,
    ),
    caller_id: (form.callerId || "").trim(),
    dialing_prefix: needsVendor(form.dialingType)
      ? normalizeDialingPrefix(form.dialingPrefix)
      : "",
    inbound_group_ids: needsInboundGroups(form.campaignType) ? (form.inboundGroupIds || []) : [],
    agent_call_routing: needsAgentCallRouting(form.dialingType, form.campaignType)
      ? normalizeAgentCallRouting(form.agentCallRouting)
      : "random",
    agent_level_routing: needsAgentCallRouting(form.dialingType, form.campaignType)
      && normalizeAgentCallRouting(form.agentCallRouting) === "agent_level"
      ? (normalizeAgentLevelRouting(form.agentLevelRouting, form.agentCallRouting) || "highest")
      : null,
    vendor_id: needsVendor(form.dialingType) && form.vendorId !== "__none__"
      ? form.vendorId
      : "",
  };

  if (needsVicidial(form.dialingType) || form.vicidialId) {
    body.vicidial_server_id = form.vicidialId || undefined;
  }

  if (needsPdServer(form.dialingType)) {
    const pdIds = selectedPdServerIds(form);
    const fallback = pdServers.length === 1 ? [pdServers[0].id] : [];
    const ids = pdIds.length ? pdIds : fallback;
    body.pd_server_ids = ids;
    body.pd_server_id = ids[0] || "";
  } else {
    body.pd_server_ids = [];
    body.pd_server_id = "";
  }
  if (needsDialLevel(form.dialingType)) {
    const n = Number(form.dialLevel);
    body.dial_level = Number.isFinite(n) && n > 0 ? n : 1;
  }
  if (needsDialRate(form.dialingType)) {
    const n = Number(form.dialRate);
    body.dial_rate = Number.isFinite(n) && n > 0 ? n : 1;
  }
  if (form.dialingType === "predictive" && form.abandonRateLimit !== "" && form.abandonRateLimit != null) {
    const ar = Number(form.abandonRateLimit);
    if (Number.isFinite(ar) && ar > 0 && ar <= 1) {
      body.abandon_rate_limit = ar;
    }
  }
  if (form.dialingType === "predictive") {
    body.pacing_use_abandon_rate_incl_amd = Boolean(form.pacingUseAbandonRateInclAmd);
  }

  if (needsAgentSession(form.dialingType)) {
    body.agent_session = form.agentSession;
  } else {
    body.agent_session = "";
  }
  if (needsUserGroups(form.dialingType)) {
    const mode = form.assignMode === "user" ? "user" : "group";
    if (mode === "user") {
      body.agent_user_ids = form.agentUserIds || [];
      body.agent_group_ids = [];
    } else {
      body.agent_group_ids = form.agentGroupIds || [];
      body.agent_user_ids = [];
    }
  } else {
    body.agent_group_ids = [];
    body.agent_user_ids = [];
  }

  if (needsLeadLists(form.dialingType)) {
    body.list_ids = form.listIds || [];
    const merged = mergeDialStatuses(form.listDialStatuses, form.systemDialStatuses);
    body.dial_statuses = merged.length
      ? merged
      : (form.dialStatuses || []).filter((s) => s && s !== "DNC");
    body.list_dial_statuses = (form.listDialStatuses || []).filter((s) => s && s !== "DNC");
    body.system_dial_statuses = (form.systemDialStatuses || []).filter((s) => s && s !== "DNC");
  } else {
    body.list_ids = [];
    body.dial_statuses = [];
    body.list_dial_statuses = [];
    body.system_dial_statuses = [];
  }

  // Always persist AMD (backend supports it on all types; form may hide UI for manual)
  applyAmdToBody(body, form);
  applyAdvancedDialingToBody(body, form);
  return body;
}

/** Visibility map for a dialing/campaign type combination (for tests / UI). */
export function fieldVisibility(dialingType, campaignType) {
  const isAi = dialingType === "ai_bot";
  return {
    vicidial: needsVicidial(dialingType),
    pdServer: needsPdServer(dialingType),
    dialLevel: needsDialLevel(dialingType),
    dialRate: needsDialRate(dialingType),
    agentSession: needsAgentSession(dialingType),
    userGroups: needsUserGroups(dialingType),
    inboundGroups: needsCampaignType(dialingType) && needsInboundGroups(campaignType),
    leadLists: needsLeadLists(dialingType),
    dialStatuses: needsLeadLists(dialingType),
    vendor: needsVendor(dialingType),
    dialingPrefix: needsVendor(dialingType),
    schedule: needsSchedule(dialingType),
    amd: needsAmd(dialingType),
    amdBasic: amdOnBasicTab(dialingType),
    amdAdvance: needsAmd(dialingType) && !amdOnBasicTab(dialingType),
    advancedDialing: needsAdvancedDialing(dialingType),
    callback: needsAdvancedDialing(dialingType),
    callerId: !isAi,
    recording: !isAi,
    telephonyIp: needsTelephonyIp(dialingType),
    mediaServer: needsMediaServer(dialingType),
    maxDuration: true,
    timezone: needsSchedule(dialingType),
    name: true,
    // Dialing type is chosen at create time; do not allow changing it on VICI Bot edit.
    dialingType: !isAi,
    campaignType: needsCampaignType(dialingType),
    agentCallRouting: needsAgentCallRouting(dialingType, campaignType),
  };
}
